import { useEffect } from 'react';
import { useSession } from '@sdk/session/SessionContext';

import { useSocketConnectionContext } from '@web/components/SocketConnectionProvider/SocketConnectionProvider';
import { useNavigate } from '@web/Router/router.hooks';
import { IncomingCommand } from '@web/components/SocketConnectionProvider/types/command';
import { IncomingData } from '@web/components/SocketConnectionProvider/types/data';
import { Values } from '@web/components/SocketConnectionProvider/types/utils';
import { useConversationMappers } from '@web/conversations/hooks/useConversationMappers';
import { useMessageConfetti } from '@web/conversations/hooks/useMessageConfetti';
import {
  useConversationActions,
  useConversationState,
} from '@web/conversations/Conversation/ConversationProvider/ConversationProvider';
import { mapEditedConversation } from '@web/conversations/utils/mappers';

// @TODO may require more elegant solution
// event data uses both `conversationId` and `id` to indicate conversation identifier,
// it happens both fields exist in the data object, in such case `conversationId` has precedence
type Wrapper = <U extends Values<IncomingData>>(
  conversationId: ID,
  fn: (data: U) => void
) => (data: U) => void;

const isActiveConversationGuard: Wrapper = (conversationId, fn) => {
  return (data) => {
    let idPropName = 'conversationId';
    if (!('conversationId' in data.msg) && 'id' in data.msg) {
      idPropName = 'id';
    }
    if (data.msg[idPropName] === +conversationId) {
      fn(data);
    }
  };
};

export const useSocketListener = () => {
  const { currentUser } = useSession();
  const {
    id,
    messages: { visibleIds },
    newestMessageId,
    selectedMessageId,
    unreadMessagesCount,
  } = useConversationState();
  const {
    addMessage,
    removeMessage,
    updateConversation,
    updateMessage,
    setReaction,
    setUnreadMessageMarker,
    setUnreadMessagesCount,
  } = useConversationActions();
  const { subscribe } = useSocketConnectionContext();
  const { mapAPIMessageStrategy } = useConversationMappers();
  const { tryConfetti } = useMessageConfetti();
  const navigate = useNavigate();

  useEffect(
    () =>
      subscribe(
        IncomingCommand.CONVERSATION_DELETE,
        isActiveConversationGuard(id, () => {
          navigate.toConversationRoot();
        })
      ),
    [id, navigate, subscribe]
  );

  useEffect(
    () =>
      subscribe(
        IncomingCommand.CONVERSATION_UPDATE,
        isActiveConversationGuard(id, ({ msg: data }) => {
          const editedConversation = mapEditedConversation(data);

          updateConversation(editedConversation);
        })
      ),
    [id, subscribe, updateConversation]
  );

  // @INFO when browsing old messages, i.e. after searching sth or redirecting to quoted messages;
  // you should not add the message to the conversation if it doesn't contain recent/newest messages
  const isMessageAddingEnabled = !selectedMessageId || visibleIds.includes(newestMessageId);
  useEffect(() => {
    if (!isMessageAddingEnabled) return;

    return subscribe(
      IncomingCommand.MESSAGE_CREATE,
      isActiveConversationGuard(id, ({ msg: data }) => {
        const mappedMessage = mapAPIMessageStrategy(data);

        addMessage({ message: mappedMessage });

        const eventSentByCurrentUser = currentUser.id === data.author.id;
        if (!eventSentByCurrentUser) {
          setUnreadMessageMarker({ message: mappedMessage });
          setUnreadMessagesCount({ count: unreadMessagesCount + 1 });

          tryConfetti(mappedMessage);
        }
      })
    );
  }, [
    addMessage,
    id,
    isMessageAddingEnabled,
    mapAPIMessageStrategy,
    setUnreadMessagesCount,
    setUnreadMessageMarker,
    subscribe,
    tryConfetti,
    unreadMessagesCount,
    currentUser.id,
  ]);

  useEffect(
    () =>
      subscribe(
        IncomingCommand.MESSAGE_UPDATE,
        isActiveConversationGuard(id, ({ msg: data }) => {
          if (data.deleted) {
            removeMessage({ messageId: data.id });
          } else {
            const mappedMessage = mapAPIMessageStrategy(data);
            updateMessage({ message: mappedMessage });
          }
        })
      ),
    [id, mapAPIMessageStrategy, removeMessage, subscribe, updateMessage]
  );

  useEffect(
    () =>
      subscribe(
        IncomingCommand.REACTION_UPDATE,
        isActiveConversationGuard(id, ({ msg: data }) => {
          const reactions = data.emojiReactions;

          setReaction({
            messageId: data.messageId,
            reactions,
          });
        })
      ),
    [id, setReaction, subscribe]
  );
};
